import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import testIds from "constants/testIds";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import "./Quotation.scss";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { getRequest } from "api/axiosClient";
import { reapproveQuotation } from "api/services/quotation";
import { useGetCostDetails } from "api/queries/quotation/useGetCostDetails";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import { formatPrice } from "helpers/commonHelpers";
import DeclineApprovalDialog from "./DeclineApprovalDialog";
import RequestApprovalDialog from "./RequestApprovalDialog";

const ManageApprovalDialog = function ({
  isOpen,
  close,
  openTransferApprovalDialog,
  shipmentId,
  fetchQuoteRequestData,
  quotationSettings,
  setShowConfirmBookQuoteDialog,
  quotationId,
  setBookingPayload,
  setBookingType,
  setShowBookQuoteDialog,
  status,
  isQuotationExpired,
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { REACT_APP_APPROVAL_REQUEST_ENDPOINT } = import.meta.env;
  const { permissions } = usePermissions();
  const { full_name } = JSON.parse(permissions);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [isFinalApproval, setIsFinalApproval] = useState(false);
  const [showRequestApprovalDialog, setShowRequestApprovalDialog] = useState(false);
  const [showDeclineApprovalDialog, setShowDeclineApprovalDialog] = useState(false);

  const quoteSelectionReasons = ["Pricing", "Timing", "Routing", "Reliability"];

  const { data: costDetailsData } = useGetCostDetails(quotationId, {
    enabled: !!quotationId,
    refetchOnMount: false,
  });

  useEffect(() => {
    setIsLoading(true);
    if (isOpen) {
      getRequest(`${REACT_APP_APPROVAL_REQUEST_ENDPOINT}?shipment_reference=${shipmentId}`)
        .then((res) => {
          setApprovalData(res.data);

          // Check if user is final approver, otherwise they'll have to request further approval
          const remaining_approver_levels = res.data.approval_flow?.remaining_approver_levels;
          if (status === "reapproval_needed_after_booking" && remaining_approver_levels?.length === 0) {
            setIsFinalApproval(true);
          } else if (remaining_approver_levels?.length === 1) {
            const is_user_final_approver = remaining_approver_levels[0][2]; // the 2nd index contains the boolean of whether the user is part of the final approval level
            setIsFinalApproval(is_user_final_approver);
          } else {
            setIsFinalApproval(false);
          }
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsLoading(false));
    }
  }, [isOpen]);

  const confirmBooking = () => {
    setIsSubmitting(true);
    reapproveQuotation(quotationId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userPendingTasks });
        const toastMessage = (
          <FormattedMessage
            id="shipment-quotes.components.manageApprovalDialog.reapprovedQuotation"
            defaultMessage="Reapproved quotation."
          />
        );
        toast(toastMessage, {
          type: "success",
        });
        queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(shipmentId) });
        close();
      })
      .catch((err) =>
        errorToast(
          err,
          <FormattedMessage
            id="shipment-quotes.components.manageApprovalDialog.couldNotReapproveQuotation"
            defaultMessage="Could not reapprove quotation. Please try again later."
          />,
        ),
      )
      .finally(() => setIsSubmitting(false));
  };

  const onTransferApproval = () => {
    const quoteSelectionReason = quoteSelectionReasons.findIndex(
      (reason) => reason === approvalData.reason_for_booking_value,
    );
    close();
    openTransferApprovalDialog(quoteSelectionReason);
  };

  const onFinalApproval = () => {
    if (status === "reapproval_needed_after_booking") {
      confirmBooking();
      return;
    }
    close();
    setShowConfirmBookQuoteDialog(true);
  };

  const onRequestFurtherApproval = () => {
    close();
    setShowRequestApprovalDialog(true);
  };

  const onDecline = () => {
    close();
    setShowDeclineApprovalDialog(true);
  };

  return (
    <>
      <DeclineApprovalDialog
        isOpen={showDeclineApprovalDialog}
        close={() => {
          setShowDeclineApprovalDialog(false);
        }}
        fetchQuoteRequestData={fetchQuoteRequestData}
        quotationId={quotationId}
      />

      <RequestApprovalDialog
        isOpen={showRequestApprovalDialog}
        close={() => {
          setShowRequestApprovalDialog(false);
        }}
        openManageApprovalDialog={() => setShowRequestApprovalDialog(true)}
        quotationId={quotationId}
        fetchQuoteRequestData={fetchQuoteRequestData}
        shipmentId={shipmentId}
        isAdditionalApproval={false}
        isBookAsDuplicate={false}
        isSuggestApproval={false}
        quotationSettings={quotationSettings}
        setBookingPayload={setBookingPayload}
        setBookingType={setBookingType}
        setShowBookQuoteDialog={setShowBookQuoteDialog}
      />
      <Dialog
        size="md"
        title={intl.formatMessage({
          id: "shipment-quotes.components.quotes.manageApproval",
          defaultMessage: "Manage Approval",
        })}
        show={isOpen}
        onHide={close}
        isLoading={isLoading}
      >
        {status === "reapproval_needed_after_booking" && (
          <Row className="mb-2">
            <Col className="w-192">
              <FormattedMessage
                id="shipment-quotes.components.manageApprovalDialog.re-approval"
                defaultMessage="This is re-approval request for"
              />{" "}
              <span className="font-weight-bold">
                {(
                  [
                    ...(costDetailsData?.costs?.post_booking_in_transit_costs_data?.["Origin Charges"] ?? []),
                    ...(costDetailsData?.costs?.post_booking_in_transit_costs_data?.Freight ?? []),
                    ...(costDetailsData?.costs?.post_booking_in_transit_costs_data?.["Destination Charges"] ?? []),
                  ]
                    ?.filter((cost) => !cost.approved_by_shipper)
                    .map(
                      (cost) =>
                        `${cost.additional_cost_description || cost.description} (${formatPrice(
                          cost.amount_target,
                          cost.target_currency_code,
                        )})`,
                    ) ?? []
                ).join(", ")}
              </span>
            </Col>
          </Row>
        )}

        <Row className="mb-2">
          <Col lg={12}>
            <p>
              <FormattedMessage
                id="shipment-quotes.components.manageApprovalDialog.thisRequestRequires"
                defaultMessage="This request requires"
              />{" "}
              <strong>
                {approvalData.approval_flow?.existing_approvers?.length +
                  approvalData.approval_flow?.remaining_approver_levels?.length +
                  (!isFinalApproval ? 1 : 0)}
              </strong>{" "}
              <FormattedMessage
                id="shipment-quotes.components.manageApprovalDialog.individualApproval"
                defaultMessage="individual approvals:"
              />
            </p>
          </Col>
        </Row>
        {approvalData.approval_flow?.existing_approvers?.map((approver) => (
          <Row>
            <Col lg={12} className="d-flex justify-content-between">
              <li className="text-success list-item">
                {`${approver.user__first_name} ${approver.user__last_name}`},{" "}
                <FormattedMessage id="general.approved" defaultMessage="Approved" />{" "}
                {moment().to(moment(approver.timestamp))}
              </li>
              <FontAwesomeIcon icon={faCheck} className="text-success" />
            </Col>
          </Row>
        ))}
        <Row>
          <Col lg={12} className="d-flex justify-content-between">
            <li className="text-secondary list-item mt-2">
              {full_name}{" "}
              <span className="text-primary font-weight-bold">
                (<FormattedMessage id="general.you" defaultMessage="You" />)
              </span>
            </li>
          </Col>
        </Row>
        {!isFinalApproval &&
          approvalData.approval_flow?.remaining_approver_levels?.map((remaining_approvers) => (
            <Row>
              <Col lg={12} className="d-flex justify-content-between">
                <li className="text-secondary list-item mt-2">{remaining_approvers[0]}</li>
              </Col>
            </Row>
          ))}
        <Row className="mb-3 mt-4">
          <Col lg={12}>
            <div className="d-block border-grey border-top w-100" />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col lg={12} className="d-flex justify-content-between">
            <div className="d-flex">
              <Button
                size="lg"
                className="btn-grey"
                label={intl.formatMessage({
                  id: "general.cancel",
                  defaultMessage: "Cancel",
                })}
                onClick={close}
              />
              {!isQuotationExpired && (
                <Button
                  className="ml-auto"
                  label={intl.formatMessage({
                    id: "shipment-quotes.components.transferApproval",
                    defaultMessage: "Transfer approval",
                  })}
                  variant="link"
                  onClick={onTransferApproval}
                />
              )}
            </div>
            <div className="d-flex">
              <Button
                size="lg"
                className="ml-3"
                variant="danger"
                label={intl.formatMessage({
                  id: "general.decline",
                  defaultMessage: "Decline",
                })}
                onClick={onDecline}
                data-testid={testIds.SHIPMENT_DASHBOARD_DECLINE_REQUEST_APPROVAL_BUTTON}
              />
              {isFinalApproval && (
                <Button
                  className="ml-3"
                  variant="success"
                  label={intl.formatMessage({
                    id: "shipment-quotes.components.manageApprovalDialog.finalApproveQuote",
                    defaultMessage: "Final approve quote",
                  })}
                  onClick={onFinalApproval}
                  disabled={isSubmitting || (isQuotationExpired && status !== "reapproval_needed_after_booking")}
                  type="submit"
                  size="lg"
                />
              )}
              {!isFinalApproval && (
                <Button
                  size="lg"
                  className="ml-3"
                  variant="success"
                  label={intl.formatMessage({
                    id: "shipment-quotes.components.manageApprovalDialog.requestFurtherApproval",
                    defaultMessage: "Request further approval",
                  })}
                  onClick={onRequestFurtherApproval}
                  type="submit"
                  data-testid={testIds.SHIPMENT_DASHBOARD_APPROVE_REQUEST_QUOTE_BUTTON}
                  disabled={isQuotationExpired}
                />
              )}
            </div>
          </Col>
        </Row>
      </Dialog>
    </>
  );
};

ManageApprovalDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  openTransferApprovalDialog: PropTypes.func.isRequired,
  shipmentId: PropTypes.string.isRequired,
  fetchQuoteRequestData: PropTypes.func.isRequired,
  quotationId: PropTypes.string,
  quotationSettings: PropTypes.instanceOf(Object).isRequired,
  status: PropTypes.string.isRequired,
  setShowConfirmBookQuoteDialog: PropTypes.func.isRequired,
  setBookingPayload: PropTypes.func.isRequired,
  setBookingType: PropTypes.func.isRequired,
  setShowBookQuoteDialog: PropTypes.func.isRequired,
  isQuotationExpired: PropTypes.bool.isRequired,
};

ManageApprovalDialog.defaultProps = {
  quotationId: PropTypes.string,
};

export default ManageApprovalDialog;
